import React from 'react'
import { domainTone, overrideDetail, statusPillLabel } from '../component/status-pill'
import { Pill, domainPillTone } from './pill'
import { credentialCheckView, credentialRejected } from './credential-check'

const DAY = 24 * 60 * 60 * 1000
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

// A section's dot in the nav: the worst verdict among its tiles.
const RANK = { ok: 1, warn: 2, bad: 3 }
const worstTone = tones => tones.reduce((worst, t) => (RANK[t] || 0) > (RANK[worst] || 0) ? t : worst, null)
// A domain that routes (verified, or allowed by an override) is fine; one
// waiting on DNS proof or at risk needs attention; a deactivated one is a
// problem.
const DOMAIN_DOT = { verified: 'ok', overridden: 'ok', pending: 'warn', at_risk: 'warn', deactivated: 'bad' }

// { [section]: 'ok' | 'warn' | 'bad' } from the tiles' `dot`s, for SectionNav.
export const sectionDots = tiles => {
  const dots = {}
  tiles.forEach(t => { if (t.dot) dots[t.section] = worstTone([dots[t.section], t.dot]) })
  return dots
}

// Each list is undefined while it loads and null when it failed (or, for
// health, when there is no row): "Loading…" and "Unavailable" respectively.
const LOADING = 'Loading…'

// Only an own-registration OIDC connection has a credential whose expiry
// EducationXR tracks. A shared app's credential is the platform's; Apple and
// Facebook have no expiry field at all, so they get no tile.
//
// The "Credential accepted" check (health.credentialCheck) sits on top: a
// credential the identity provider refuses is the tile's news whatever its
// expiry says, and an accepted one carries an Accepted badge.
const credentialTile = (provider, now, health) => {
  const tile = expiryTile(provider, now)
  const check = health && health.credentialCheck
  if (!tile || !check) return tile
  const view = credentialCheckView(check)
  if (credentialRejected(check)) return { ...tile, value: view.label, detail: check.summary, tone: 'bad', dot: 'bad', pill: true, badge: undefined }
  if (check.status === 'accepted') return { ...tile, badge: { tone: 'ok', label: view.label, title: check.summary } }
  return tile
}

const expiryTile = (provider, now) => {
  if (provider.driver === 'apple' || provider.driver === 'facebook') return null
  if (provider.connection === 'shared_app') return { key: 'credential', label: 'credential', value: 'Managed by EducationXR', detail: 'the shared app\'s credential', tone: null, dot: null, section: 'creds' }
  if (!provider.expiresAt) return { key: 'credential', label: 'credential', value: 'No expiry set', tone: null, dot: null, section: 'creds' }
  const expiresAtMs = new Date(provider.expiresAt).getTime()
  if (Number.isNaN(expiresAtMs)) return { key: 'credential', label: 'credential', value: 'Unavailable', tone: null, dot: null, section: 'creds' }
  const label = provider.clientAuthMethod === 'private_key_jwt' ? 'certificate' : 'client secret'
  const days = Math.ceil((expiresAtMs - now) / DAY)
  return days <= 0
    ? { key: 'credential', label, value: 'Expired', detail: new Date(provider.expiresAt).toLocaleDateString(), tone: 'bad', dot: 'bad', section: 'creds' }
    : { key: 'credential', label, value: `${days} days left`, detail: `expires ${new Date(provider.expiresAt).toLocaleDateString()}`, tone: days <= 30 ? 'warn' : null, dot: days <= 30 ? 'warn' : 'ok', section: 'creds' }
}

// The identity provider tile's problems: the health row's errors less the
// rejected-credential one, which the credential tile reports instead.
const idpErrors = health => {
  const check = health.credentialCheck
  return (health.errors || []).filter(e => !(check && credentialRejected(check) && e === check.summary))
}

export const connectionTiles = ({ provider = {}, health, domains, links, mappings, now = Date.now() }) => {
  const tiles = []

  if (health === undefined) tiles.push({ key: 'idp', label: 'identity provider', value: 'Checking…', section: 'idp', tone: null })
  else if (!health) tiles.push({ key: 'idp', label: 'identity provider', value: 'Unavailable', section: 'idp', tone: null })
  else if (idpErrors(health).length) tiles.push({ key: 'idp', label: 'identity provider', value: 'Problem', detail: idpErrors(health)[0], tone: 'bad', dot: 'bad', pill: true, section: 'idp' })
  else {
    const mismatch = Boolean(health.discovery && health.discovery.issuerMatches === false)
    tiles.push({ key: 'idp', label: 'identity provider', value: 'Reachable', detail: mismatch ? 'issuer does not match exactly' : 'issuer matches', tone: mismatch ? 'warn' : 'ok', dot: mismatch ? 'warn' : 'ok', pill: true, section: 'idp' })
  }

  const credential = credentialTile(provider, now, health)
  if (credential) tiles.push(credential)

  if (domains === undefined) tiles.push({ key: 'domains', label: 'domains', value: LOADING, tone: null, section: 'routing' })
  else if (!domains) tiles.push({ key: 'domains', label: 'domains', value: 'Unavailable', tone: null, section: 'routing' })
  // No domain: a public sign-in method is offered to everyone; anything else
  // reaches nobody (audit B3), which matters while it's switched on.
  else if (!domains.length) {
    tiles.push(provider.public
      ? { key: 'domains', label: 'domains', value: '0', detail: 'public sign-in method', tone: null, dot: null, section: 'routing' }
      : { key: 'domains', label: 'domains', value: '0', detail: 'nobody is sent here yet', tone: provider.active ? 'warn' : null, dot: provider.active ? 'warn' : null, section: 'routing' })
  }
  else {
    const first = domainTone(domains[0], now)
    tiles.push({
      key: 'domains',
      label: 'domains',
      value: String(domains.length),
      // the first domain's state, as the pill the routing section shows for it
      badge: { tone: domainPillTone(first), label: statusPillLabel(first), title: overrideDetail(domains[0]) || undefined },
      detail: domains.length > 1 ? `${domains[0].domain} and ${domains.length - 1} more` : domains[0].domain,
      tone: null,
      dot: worstTone(domains.map(d => DOMAIN_DOT[domainTone(d, now)] || null)),
      section: 'routing'
    })
  }

  if (links === undefined) tiles.push({ key: 'orgs', label: 'organizations', value: LOADING, tone: null, section: 'routing' })
  else if (!links) tiles.push({ key: 'orgs', label: 'organizations', value: 'Unavailable', tone: null, section: 'routing' })
  else tiles.push({ key: 'orgs', label: 'organizations', value: `${links.length} linked`, detail: `${links.filter(l => l.matchData && l.matchData.claim).length} with a claim rule`, tone: null, section: 'routing' })

  if (mappings === undefined) tiles.push({ key: 'mappings', label: 'role mappings', value: LOADING, tone: null, section: 'claims' })
  else if (!mappings) tiles.push({ key: 'mappings', label: 'role mappings', value: 'Unavailable', tone: null, section: 'claims' })
  else tiles.push({ key: 'mappings', label: 'role mappings', value: String(mappings.length), detail: `on ${plural(new Set(mappings.map(m => m.claimName)).size, 'claim')}`, tone: null, section: 'claims' })

  tiles.push(provider.endIdpSessionOnSignout
    ? { key: 'signout', label: 'sign-out', value: 'Also the identity provider', detail: 'ends their identity provider sign-in too', tone: null, section: 'signout' }
    : { key: 'signout', label: 'sign-out', value: 'EducationXR only', detail: 'their identity provider stays signed in', tone: null, section: 'signout' })

  return tiles
}

// A tile's value is plain text, text in its tone, or (pill: true) the tone's
// pill; a badge is a pill after the value (the domains tile's state).
export const StatusStrip = ({ tiles, onGo }) =>
  <div className="sw-strip" role="region" aria-label="connection status">
    {tiles.map(tile =>
      <button key={tile.key} type="button" className={tile.nowrap ? 'sw-tile-name' : undefined} onClick={() => onGo && onGo(tile.section)}>
        <span className="sw-k">{tile.label}</span>
        {tile.pill
          ? <span className="sw-v"><Pill tone={tile.tone}>{tile.value}</Pill></span>
          : <span className={`sw-v ${tile.tone || ''}`.trim()}>
            {tile.value}
            {tile.badge && tile.badge.label && <Pill tone={tile.badge.tone} title={tile.badge.title}>{tile.badge.label}</Pill>}
          </span>}
        {tile.detail && <span className="sw-d">{tile.detail}</span>}
      </button>)}
  </div>
